// ForgotPasswordForm.jsx
import React, { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import axios from "axios";
import config from "../../config.js";

const baseURL = config.baseURL;

const ForgotPasswordForm = ({ loading, setLoading, handleBackToLogin }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isSubmitted, setIsSubmitted] = useState(false); // Track form submission

  const validateEmailForm = () => {
    let isValid = true;
    setEmailError("");

    if (!email) {
      setEmailError("Email is required");
      isValid = false;
    }
    return isValid;
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError("");
    setMessage({ type: "", text: "" });
    setIsSubmitted(false); // Re-enable button when email changes
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmailForm()) return;

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await axios.post(`${baseURL}/user/forgot/password`, {
        email,
      });

      if (response.data?.message === "No user registered with this email") {
        setMessage({
          type: "error",
          text: "No account found with this email address. Please check and try again.",
        });
        return;
      }

      setMessage({
        type: "success",
        text: "Password reset link has been sent to your email. Please check your inbox.",
      });
      setIsSubmitted(true); // Disable button on success
    } catch (error) {
      if (error.response?.data?.message) {
        setMessage({
          type: "error",
          text: error.response.data.message,
        });
      } else if (error.request) {
        setMessage({
          type: "error",
          text: "Unable to connect to server. Please check your internet connection.",
        });
      } else {
        setMessage({
          type: "error",
          text: "Something went wrong. Please try again later.",
        });
      }
      setIsSubmitted(true);
      console.error("Password reset error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      autoComplete="off"
      onSubmit={handleForgotPasswordSubmit}
      style={{ padding: "1rem" }}
    >
      {message.type === "success" ? (
        // ✅ SUCCESS UI
        <div style={{ textAlign: "center", padding: "0.5rem" }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Check Your Email
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            gutterBottom
            sx={{ fontSize: "16px", textAlign: "center" }}
          >
            We've sent a link to reset your password. Please check your inbox
            and follow the instructions.
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginTop: "1.8rem", fontSize: "14px" }}
          >
            Didn't receive the message?{" "}
            <span
              onClick={() => {
                setEmail("");
                setMessage({ type: "", text: "" });
                setIsSubmitted(false);
              }}
              style={{
                color: "#007BFF",
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              Try again.
            </span>
          </Typography>
        </div>
      ) : (
        // ✅ FORM UI
        <>
          {message.text && (
            <Alert
              severity={message.type}
              sx={{
                mt: 2,
                mb: 2,
                borderRadius: "8px",
              }}
            >
              {message.text}
            </Alert>
          )}
          <div style={{ textAlign: "center", padding: "0.5rem" }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Forgot Password?
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              gutterBottom
              sx={{ fontSize: "16px", textAlign: "center" }}
            >
              Resetting your password is easy. Just enter the email you used to
              register.
            </Typography>
          </div>

          <Typography variant="h6">Email</Typography>
          <TextField
            id="email"
            placeholder="Type Email"
            type="text"
            variant="outlined"
            fullWidth
            className="form_field"
            autoComplete="new-email"
            error={!!emailError}
            helperText={emailError || " "}
            onChange={handleEmailChange}
            value={email}
            InputProps={{
              sx: {
                borderRadius: "16px",
                border: "2px solid rgba(24, 48, 89, 1)",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              },
            }}
          />

          {/* <Typography
            variant="body2"
            style={{ marginTop: "20px", marginBottom: "20px" }}
          >
            Don't worry. Resetting your password is easy, just tell us the email
            address you registered with RouteEye.
          </Typography> */}

          <Button
            className="customButton_add"
            variant="contained"
            type="submit"
            disabled={loading || isSubmitted || !email}
            sx={{
              width: "100%",
              borderRadius: "16px",
              marginTop: "25px",
              backgroundColor: "#FFFF00",
              color: "#000000",
              boxShadow: "none",
              height: "56px",
              position: "relative",
              "&:hover": {
                backgroundColor: "#FFFF00",
                boxShadow: "none",
              },
              "&:disabled": {
                backgroundColor: "#F5F5F5",
                color: "#666666",
              },
            }}
          >
            {loading ? (
              <CircularProgress
                size={28}
                sx={{ color: "#000000", position: "absolute" }}
              />
            ) : (
              <span
                style={{
                  textTransform: "none",
                  fontWeight: "600",
                  fontSize: "32px",
                }}
              >
                {isSubmitted ? "Sent" : "Send"}
              </span>
            )}
          </Button>
        </>
      )}
    </form>
  );
};

export default ForgotPasswordForm;
